/**
 * SourcesChart - Interactive pie chart for traffic sources
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { useState, useCallback } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Sector,
  TooltipProps,
} from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { TopSource } from '@/types/customer';
import { CHART_COLORS } from '@/services/customer/constants';
import { formatCompactNumber, formatPercentage } from '@/services/customer/formatters';

interface Props {
  data: TopSource[];
  onSegmentClick?: (source: string, medium: string, sessions: number) => void;
}

const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const total = payload[0].payload.totalSessions;
  const percentage = (data.value / total) * 100;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
        {data.name}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption">
          Sessions: <strong>{formatCompactNumber(data.value)}</strong>
        </Typography>
      </Box>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatPercentage(percentage / 100)} of total
        </Typography>
      </Box>
    </Paper>
  );
};

const renderActiveShape = (props: any) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle,
    fill,
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))' }}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 12}
        outerRadius={outerRadius + 14}
        fill={fill}
      />
    </g>
  );
};

function SourcesChart({ data, onSegmentClick }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const totalSessions = data.reduce((sum, source) => sum + source.sessions, 0);

  const chartData = data.map((source, index) => ({
    name: `${source.source} / ${source.medium}`,
    value: source.sessions,
    color: CHART_COLORS[index % CHART_COLORS.length],
    source: source.source,
    medium: source.medium,
    totalSessions,
  }));

  const handleClick = useCallback((_chartEntry: any, index: number) => {
    if (onSegmentClick && chartData[index]) {
      const segment = chartData[index];
      onSegmentClick(segment.source, segment.medium, segment.value);
    }
  }, [onSegmentClick, chartData]);

  const onPieEnter = useCallback((_: any, index: number) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          activeIndex={activeIndex !== null ? activeIndex : undefined}
          activeShape={renderActiveShape}
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          onClick={handleClick}
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          animationDuration={500}
        >
          {chartData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={entry.color}
              cursor={onSegmentClick ? 'pointer' : 'default'}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          layout="vertical"
          verticalAlign="middle"
          align="right"
          iconType="circle"
          iconSize={10}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default SourcesChart;
